import React, { useCallback, useMemo } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { connect } from 'react-redux';
import { rectSortingStrategy } from '@dnd-kit/sortable';
import Grid from '@material-ui/core/Grid';
import IconButton from '../IconButton';
import SortableList from '../SortableList';
import OptionModal from '../OptionModal';
import ShopModal from '../../components/ShopModal';
import Spinner from '../Spinner';
import { styles } from './OptionList.styles';
import * as actions from '../../actions/index';
import { checkAccess } from '../../utils/admins';
import { handleSynchronisationToggle } from '../../utils/shopUtils';
import { checkIntegrationAccess } from 'src/utils/externalIntegration';
import { Option, Category, Section } from '../../models/Menu';
import { useOptionList } from './useOptionList';
import { handleRemoveOption } from './OptionList.services.hooks';
import { Shop } from 'src/models/shop';
import { RootState, ModalData, OptionListState } from './OptionList.types';

export interface OptionListProps extends WithStyles<typeof styles> {
    categoryKey: string;
    options: Record<string, Option>;
    allOptions?: Record<string, Option>;
    categories: Record<string, Category>;
    sections: Record<string, Section>;
    shops: Record<string, Shop>;
    shopId: string;
    nbShops: number;
    onActionComplete?: () => void;
    showModal: (modalData: ModalData | null) => void;
    openErrorModal: (error: string) => void;
}

const OptionList: React.FC<OptionListProps> = (props) => {
    const {
        classes,
        categoryKey,
        categories,
        shopId,
        onActionComplete,
        sections
    } = props;

    const {
        state,
        setState,
        sortedOptions,
        handleOpenModal,
        handleCloseModal,
        handleDrag,
        handleStart,
        renderOptionItem
    } = useOptionList(props);

    const { option, showOptionModal, createOption, activeId, showModal, selectedShops, isLoading } = state;

    const disabled = checkIntegrationAccess('option', 'hideCreateOption', shopId);

    const mergeSetState = useCallback((newState: Partial<OptionListState> | ((prev: OptionListState) => Partial<OptionListState>)) => {
        setState((prev) => {
            const updates = typeof newState === 'function' ? newState(prev) : newState;
            return { ...prev, ...updates };
        });
    }, [setState]);

    const componentProxy = useMemo(() => ({
        state,
        setState: mergeSetState,
        props
    }), [state, mergeSetState, props]);

    const handleRemove = useCallback(() => {
        handleRemoveOption(option, props, state, setState, onActionComplete);
    }, [option, props, state, setState, onActionComplete]);

    if (!sections) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <div>
            <Grid container className={classes.container}>
                <div
                    aria-hidden='true'
                    role='button'
                    onClick={() =>
                        (checkAccess('features', 'productAndOption') && !disabled && handleOpenModal({}))
                    }
                    className={classes.firstItem}
                >
                    <IconButton
                        className={classes.addIcon}
                        title='Ajouter une option'
                        disabled={!checkAccess('features', 'productAndOption') || disabled}
                    >
                        <AddIcon className={classes.iconStyle} />
                    </IconButton>
                </div>
                <SortableList
                    list={sortedOptions}
                    activeId={activeId}
                    strategy={rectSortingStrategy}
                    handleDragEnd={(e) => handleDrag(e, sortedOptions)}
                    handleDragStart={handleStart}
                    renderItem={renderOptionItem}
                    handleDragMove={() => { }}
                />
                {showOptionModal && (
                    <OptionModal
                        createOption={createOption}
                        isOpen={showOptionModal}
                        category={categories[categoryKey]}
                        option={option}
                        closeModal={handleCloseModal}
                    />
                )}
                {showModal && (
                    <ShopModal
                        shopId={shopId}
                        showModal={showModal}
                        selectedShops={selectedShops}
                        loading={isLoading}
                        onClick={handleRemove}
                        handleToggle={handleSynchronisationToggle}
                        component={componentProxy}
                        text={'Sélectionnez les magasins dont vous voulez supprimer cette option:'}
                        title={'Supprimer votre option:'}
                        isSuppression={true}
                    />
                )}
            </Grid>
        </div>
    );
};

function mapStateToProps({ firebaseRealTime, user }: RootState) {
    const { shops = {}, menu } = firebaseRealTime;
    const { nbShops, shopId } = user;
    const menuData = menu || { categories: {}, sections: {} };
    const { categories = {}, sections } = menuData;
    return {
        categories,
        sections: sections ?? null,
        shops,
        shopId,
        nbShops
    };
}

export default connect(mapStateToProps, actions)(withStyles(styles)(OptionList));
